'use client'

import { addDays, formatISO } from 'date-fns'

import { type WidgetState } from './use-widget-state'

/**
 * The submitted-search store, **shared with the legacy booking application**.
 *
 * Both the key and the record shape belong to that application — it has written this
 * key on every valid submit for far longer than nexus has existed — so this module
 * adapts to it rather than the other way round. That direction is deliberate: it keeps
 * the dependency pointing at the application we are migrating *off*, needs no change
 * there, and works retroactively for a visitor who has already searched in it.
 *
 * Written **only on submission** (`use-search-submit.ts`), which is the same trigger the
 * legacy application uses, so neither side can surprise the other with a shape it would
 * not have produced. Nothing writes here on mount: a selection a visitor never submitted
 * is page context, and publishing that over their real search is the defect this
 * arrangement exists to avoid.
 */
export const SHARED_STORAGE_KEY = 'widgetSearchHistory_v2'

/**
 * A key earlier nexus builds wrote the widget selection under. Nothing reads or writes it,
 * and nothing expires it either — its 24-hour expiry ran in the reader that went with
 * it — so without an explicit removal a visitor who loaded a widget page on one of those
 * builds keeps a dead entry forever.
 */
const RETIRED_STORAGE_KEY = 'wanderu-widget-state'

/** The legacy cache window, matching that application's `SEARCH_CACHE_DAYS`. */
const CACHE_DAYS = 5

/** Seat counts the widget can render; a value outside this is not selectable. */
const MIN_PASSENGERS = 1
const MAX_PASSENGERS = 8

const MODES: readonly WidgetState['mode'][] = ['bus', 'train', 'ferry', 'flight']
const TRIP_TYPES: readonly WidgetState['tripType'][] = ['one-way', 'round-trip']

/**
 * One record as the legacy application writes it. Every field is optional because a
 * writer outside this repository produces it — nothing here may assume its presence.
 * `mode` and `tripType` are nexus's additions: that application reads this record with a
 * named destructure over a spread nothing else touches, so unknown keys are inert to it,
 * and without them nexus would lose the transport mode on every mount.
 */
interface SharedSearchRecord {
    originInput?: string
    originPlaceId?: string
    destinationInput?: string
    destinationPlaceId?: string
    passengersInput?: string
    departDate?: string
    returnDate?: string
    createdAt?: string
    expiresAt?: string
    mode?: WidgetState['mode']
    tripType?: WidgetState['tripType']
}

/**
 * Whether the record has outlived its own `expiresAt`.
 *
 * A missing or unparseable stamp counts as **live**, matching the legacy reader — which
 * reaches that by two different routes. An unparseable stamp gives it an invalid moment,
 * and nothing is ever `isAfter` an invalid moment. A missing one gives it
 * `moment(undefined)`, which *is* the current time; what saves the record there is that
 * it constructs its own `now` on the line before (`utils.js:59-61`). Being stricter than
 * the other writer would mean discarding records it still honours.
 */
function isExpired(expiresAt: string | undefined): boolean {
    if (!expiresAt) {
        return false
    }

    const deadline = Date.parse(expiresAt)
    return !Number.isNaN(deadline) && Date.now() > deadline
}

/** The seat count, or `undefined` when the stored value is not one the widget can show. */
function readPassengers(raw: string | undefined): number | undefined {
    if (!raw) {
        return undefined
    }

    const parsed = Number(raw)
    if (!Number.isInteger(parsed)) {
        return undefined
    }

    // Clamped rather than dropped: the legacy application's own limit may differ from
    // ours, and an out-of-range count is still a real expression of party size.
    return Math.min(Math.max(parsed, MIN_PASSENGERS), MAX_PASSENGERS)
}

/** A calendar date in the form every nexus date consumer parses. */
const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/

/**
 * The calendar date a stored value means, or `undefined` when it is not one the widget
 * can render.
 *
 * The other application writes its `departDate` state verbatim, and that state defaults
 * to `i18nMoment().format()` — a full ISO datetime carrying an offset, which is what the
 * most ordinary flow there stores (keep the prefilled date, search). Only its pickers
 * emit `YYYY-MM-DD`. Our date consumers split on `-`, so an untrimmed value reaches
 * `new Date(NaN)` and renders the literal string "Invalid Date".
 *
 * Narrowed rather than dropped: that application formats in its own local zone instead
 * of normalizing to UTC, so the leading date part is the day the visitor actually chose.
 */
function readDate(raw: string | undefined): string | undefined {
    if (!raw) {
        return undefined
    }

    const [date] = raw.split('T')
    return DATE_ONLY.test(date) ? date : undefined
}

/**
 * The stored submitted search, as a **partial** selection, or `null` when there is
 * nothing usable. Partial and free of `undefined` values so the caller can spread it
 * over its defaults without blanking them.
 */
export function readSharedSelection(): Partial<WidgetState> | null {
    if (typeof window === 'undefined') {
        return null
    }

    let raw: string | null
    try {
        // Retired here, on the one path every surface mounting the widget runs, so the
        // superseded entry clears itself across the population within a visit.
        localStorage.removeItem(RETIRED_STORAGE_KEY)
        raw = localStorage.getItem(SHARED_STORAGE_KEY)
    } catch {
        // Storage unavailable (private mode, blocked cookies) — degrade to "nothing
        // stored". A `SecurityError` escaping here would propagate out of the restore
        // effect and replace the page with `error.tsx` on every surface that mounts the
        // widget, so this catch is load-bearing, not defensive tidiness.
        return null
    }

    if (!raw) {
        return null
    }

    let record: SharedSearchRecord
    try {
        const parsed: unknown = JSON.parse(raw)
        if (typeof parsed !== 'object' || parsed === null) {
            return null
        }
        record = parsed as SharedSearchRecord
    } catch {
        // Left in place, not removed: another application owns this key, and content
        // nexus cannot read may still be meaningful to it.
        return null
    }

    if (isExpired(record.expiresAt)) {
        return null
    }

    const selection: Partial<WidgetState> = {}

    if (record.originInput) {
        selection.from = record.originInput
    }
    if (record.originPlaceId) {
        selection.fromId = record.originPlaceId
    }
    if (record.destinationInput) {
        selection.to = record.destinationInput
    }
    if (record.destinationPlaceId) {
        selection.toId = record.destinationPlaceId
    }
    const departDate = readDate(record.departDate)
    if (departDate) {
        selection.departDate = departDate
    }
    const returnDate = readDate(record.returnDate)
    if (returnDate) {
        selection.returnDate = returnDate
    }
    if (record.mode && MODES.includes(record.mode)) {
        selection.mode = record.mode
    }

    const passengers = readPassengers(record.passengersInput)
    if (passengers !== undefined) {
        selection.passengers = passengers
    }

    // The legacy shape has no trip-type field, so a record that application wrote
    // carries none. Derive it from the return date as it does — from the **narrowed**
    // one, which is the one deliberate divergence: a return date we cannot read would
    // otherwise leave the widget in round-trip mode with nothing to show in the field.
    selection.tripType =
        record.tripType && TRIP_TYPES.includes(record.tripType)
            ? record.tripType
            : returnDate
              ? 'round-trip'
              : 'one-way'

    return selection
}

/**
 * Publish a **submitted** selection for the legacy booking application, in its shape.
 * Call only once the selection is valid — every caller reaches this through
 * `use-search-submit`, which has already established that.
 */
export function writeSharedSelection(state: WidgetState): void {
    if (typeof window === 'undefined') {
        return
    }

    const now = new Date()
    const record: SharedSearchRecord = {
        createdAt: formatISO(now),
        expiresAt: formatISO(addDays(now, CACHE_DAYS)),
        departDate: state.departDate,
        destinationInput: state.to,
        destinationPlaceId: state.toId,
        originInput: state.from,
        originPlaceId: state.fromId,
        passengersInput: String(state.passengers),
        mode: state.mode,
        tripType: state.tripType,
    }

    // A one-way selection must leave the field out entirely, not write an empty value —
    // the legacy reader treats a present return date as "round trip".
    if (state.tripType === 'round-trip' && state.returnDate) {
        record.returnDate = state.returnDate
    }

    try {
        localStorage.setItem(SHARED_STORAGE_KEY, JSON.stringify(record))
    } catch {
        // Private-mode / quota — non-fatal, and the publish is best-effort. This runs
        // inside the submit handler before it returns the `/depart/` URL, so a throw
        // escaping here would leave the Search button doing nothing at all.
    }
}
